import { useCallback, useEffect, useRef, useState } from "react";
import { acquireBandLeaderChannel, releaseBandLeaderChannel } from "../realtime/bandLeaderChannel";

export default function useLeaderTransportBroadcast({ bandId }) {
  const [isOnline, setIsOnline] = useState(() => navigator?.onLine ?? true);
  const channelRef = useRef(null);
  const channelBandIdRef = useRef(null);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    if (!bandId || !isOnline) {
      if (channelRef.current) {
        releaseBandLeaderChannel(channelBandIdRef.current);
        channelRef.current = null;
        channelBandIdRef.current = null;
      }
      return;
    }
    const channel = acquireBandLeaderChannel({ bandId });
    channelRef.current = channel;
    channelBandIdRef.current = bandId;
    return () => {
      if (channelRef.current) {
        releaseBandLeaderChannel(channelBandIdRef.current);
        channelRef.current = null;
        channelBandIdRef.current = null;
      }
    };
  }, [bandId, isOnline]);

  const sendBroadcast = useCallback(
    (event, payload) => {
      if (!channelRef.current || !isOnline) return;
      channelRef.current.send(event, payload);
    },
    [isOnline],
  );

  const sendPresence = useCallback(
    (payload) => {
      sendBroadcast("leader_presence", payload);
    },
    [sendBroadcast],
  );

  const sendSongChange = useCallback(
    (payload) => {
      sendBroadcast("leader_song_change", payload);
    },
    [sendBroadcast],
  );

  const sendPlay = useCallback(
    (payload) => {
      sendBroadcast("leader_play", payload);
    },
    [sendBroadcast],
  );

  const sendJump = useCallback(
    (payload) => {
      sendBroadcast("leader_jump", payload);
    },
    [sendBroadcast],
  );

  const sendStop = useCallback(
    (payload) => {
      sendBroadcast("leader_stop", payload);
    },
    [sendBroadcast],
  );

  return {
    sendPresence,
    sendSongChange,
    sendPlay,
    sendJump,
    sendStop,
    isOnline,
  };
}
